// =============================================================
//  ForgotPasswordScreen — Réinitialisation du mot de passe
//  Refonte split-screen, logique Firebase intacte.
// =============================================================

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, Alert } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { auth } from '../private/firebaseConfig';
import { sendPasswordResetEmail } from 'firebase/auth';

import AuthLayout from './auth-ui/AuthLayout';
import { Input, Button } from '../admin/web-ui/kit';
import { colors, spacing } from '../admin/web-ui/theme';

export default function ForgotPasswordScreen({ navigation }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

  const getErrorMessage = (code) => {
    switch (code) {
      case 'auth/invalid-email': return 'Adresse email invalide';
      case 'auth/user-not-found': return 'Aucun compte trouvé avec cet email';
      case 'auth/too-many-requests': return 'Trop de tentatives. Réessaie plus tard.';
      case 'auth/network-request-failed': return 'Erreur de connexion réseau';
      default: return 'Une erreur est survenue';
    }
  };

  const showAlert = (title, message) => {
    if (Platform.OS === 'web') window.alert(`${title}\n\n${message}`);
    else Alert.alert(title, message);
  };

  const handleResetPassword = async () => {
    setError('');
    setSuccess(false);

    if (!email.trim()) {
      setError('Entre ton adresse email');
      return;
    }
    if (!validateEmail(email)) {
      setError("Format d'email invalide");
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setSuccess(true);
      showAlert(
        'Email envoyé',
        "Un lien de réinitialisation t'a été envoyé. Vérifie aussi tes spams."
      );
    } catch (err) {
      console.error('Reset password error:', err);
      if (err.code === 'auth/user-not-found') {
        // Par sécurité on ne révèle pas l'existence du compte
        setSuccess(true);
        showAlert(
          'Vérifie ta boîte mail',
          'Si cette adresse correspond à un compte, tu recevras un lien de réinitialisation.'
        );
      } else {
        setError(getErrorMessage(err.code));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Mot de passe oublié"
      subtitle="Pas de panique. Indique ton email et on t'envoie un lien pour le réinitialiser."
      tagline={'On t\'aide à retrouver\nton accès en deux clics.'}
      description="Tu recevras un email avec un lien sécurisé pour créer un nouveau mot de passe."
      footer={
        <View style={styles.footerRow}>
          <Text style={styles.footerText}>Tu as retrouvé ton mot de passe ?</Text>
          <TouchableOpacity onPress={() => navigation.navigate('LoginScreen')}>
            <Text style={styles.footerLink}>Se connecter</Text>
          </TouchableOpacity>
        </View>
      }
    >
      {!!error && (
        <View style={styles.errorBox}>
          <MaterialCommunityIcons name="alert-circle-outline" size={16} color={colors.danger} />
          <Text style={styles.errorBoxText}>{error}</Text>
        </View>
      )}

      {success && (
        <View style={styles.successBox}>
          <MaterialCommunityIcons name="check-circle-outline" size={16} color={colors.success} />
          <Text style={styles.successBoxText}>
            Vérifie ta boîte mail (et les spams) pour le lien de réinitialisation.
          </Text>
        </View>
      )}

      <View style={{ marginBottom: spacing.md }}>
        <Text style={styles.label}>Email</Text>
        <Input
          icon="email-outline"
          value={email}
          onChangeText={setEmail}
          placeholder="ton.email@exemple.com"
          type="email"
          autoCapitalize="none"
        />
      </View>

      <Button
        icon="email-fast-outline"
        onPress={handleResetPassword}
        loading={loading}
        fullWidth
        style={{ marginTop: 8 }}
      >
        Envoyer le lien
      </Button>

      <TouchableOpacity
        onPress={() => navigation.navigate('LoginScreen')}
        style={styles.backBtn}
      >
        <MaterialCommunityIcons name="arrow-left" size={16} color={colors.textSecondary} />
        <Text style={styles.backText}>Retour à la connexion</Text>
      </TouchableOpacity>
    </AuthLayout>
  );
}

const styles = StyleSheet.create({
  label: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 6,
  },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.dangerSoft,
    borderWidth: 1,
    borderColor: colors.danger + '40',
    borderRadius: 10,
    padding: 12,
    marginBottom: spacing.md,
  },
  errorBoxText: { flex: 1, fontSize: 13, color: colors.danger, fontWeight: '600' },
  successBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.successSoft,
    borderWidth: 1,
    borderColor: colors.success + '40',
    borderRadius: 10,
    padding: 12,
    marginBottom: spacing.md,
  },
  successBoxText: { flex: 1, fontSize: 13, color: colors.success, fontWeight: '600' },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: spacing.md,
    padding: 8,
  },
  backText: { color: colors.textSecondary, fontSize: 13, fontWeight: '600' },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  footerText: { color: colors.textSecondary, fontSize: 13 },
  footerLink: { color: colors.primary, fontSize: 13, fontWeight: '700' },
});
